import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { Confirm, Icons, Modal, PercentsSpinner, Select, UnavailableFeatureMessage } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';
import BranchTable from '@components/routes/CmModule/ConfigurationComparison/BranchTable';
import PresetCcForm from '@components/routes/CmModule/ConfigurationComparison/ComparisonOptions/PresetCCForm';
import {
	TOnSubmit,
} from '@components/routes/CmModule/ConfigurationComparison/ComparisonOptions/PresetCCForm/PresetCCForm';

import { useConfigurationComparison } from '@hooks/cmModule/useConfigurationComparison';
import { FULL_PERCENT } from '@hooks/constants';
import { useVendorDrivenFeatureAvailability } from '@hooks/utils/useVendorDrivenFeatureAvailability';

import { configurationManagement, moBranches as moBranchesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSnackbar } from '@slices/common';
import { resetMoBranchTable } from '@slices/moBranch';
import {
	resetMoBranchesTable,
	setPage,
	setPageSize,
	setSearchValue,
	setSort,
} from '@slices/moBranches';

import { isEmpty } from '@utils/common';
import { formatDate } from '@utils/date';
import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { IBranchTable } from '@interfaces/cmModule/configurationComparison';
import { TIdName } from '@interfaces/common/common';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort } from '@interfaces/components/table';
import { ICategoryPath, ITreePreset } from '@interfaces/components/tree';
import { ISubmittedParamsProps } from './interfaces';
import { IModalSelectOption } from '@components/common/ModalSelect/ModalSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import { EAdminPanelPageTitles, EMenuActionsTypes, ESnackbarTypes, EVendorsValues } from '@enums/common';
import { EValidationMode } from '@enums/validationMode';
import { EParametersFormValues } from './enums';

import { responseMessages } from '@constants/responseMessages';
import { searchTooltipText } from './constants';

import ComparisonOptions from './ComparisonOptions';
import { getColumns } from './manageMoBranches';
import MOTree from './MOTree';

import styles from './ConfigurationComparison.module.scss';

const ConfigurationComparison = () => {
	const dispatch = useAppDispatch();
	const { selectedVendor } = useAppSelector(configurationManagement);

	const {
		formState: { errors },
		handleSubmit,
		setValue,
		watch,
		control,
		register,
		resetField,
	} = useForm<FieldValues>({
		mode: EValidationMode.onSubmit,
		defaultValues: {
			[EParametersFormValues.vendor]: null,
			[EParametersFormValues.technology]: null,
			[EParametersFormValues.version]: null,
			[EParametersFormValues.type]: null,
			[EParametersFormValues.nodeType]: null,
			[EParametersFormValues.sites]: [],
			[EParametersFormValues.nodes]: [],
			[EParametersFormValues.dataSourceCreatedAtFirst]: null,
			[EParametersFormValues.dataSourceCreatedAtSecond]: null,
			[EParametersFormValues.isFirstCurrentDate]: false,
			[EParametersFormValues.isSecondCurrentDate]: false,
		},
	});

	const [isOpenComparisonOptions, setIsOpenComparisonOptions] = useState(true);
	const [isOpenMOTree, setIsOpenMOTree] = useState(false);
	const [isOpenDeleteConfirmPreset, setIsOpenDeleteConfirmPreset] = useState(false);
	const [idForDeletePreset, setIdForDeletePreset] = useState<string>('');
	const [isDifferenceOnly, setIsDifferenceOnly] = useState(false);
	const [isDirtyMoTree, setIsDirtyMoTree] = useState(true);
	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [editablePreset, setEditablePreset] = useState<TIdName | undefined>();

	const [selectedTableBranch, setSelectedTableBranch] = useState<IBranchTable>();
	const [submittedParams, setSubmittedParams] = useState<ISubmittedParamsProps>();
	const [submittedPath, setSubmittedPath] = useState<Array<ICategoryPath>>();
	const [selectedPreset, setSelectedPreset] = useState<TSelectOptionType | null>(null);
	const [savedPreset, setSavedPreset] = useState<ITreePreset | undefined>();
	const [isPresetTableView, setIsPresetTableView] = useState<boolean>(false);

	const clearSelectPreset = () => {
		setSelectedPreset(null);
		setSavedPreset(undefined);
	};

	const clearResetPreset = () => {
		setIsDirtyMoTree(true);
		setIsPresetTableView(false);
	};

	const clearAllPreset = useCallback(() => {
		if (!selectedPreset) return;

		clearSelectPreset();
		clearResetPreset();
		setSubmittedParams(undefined);
	}, [selectedPreset]);

	const handleSubmitPath = useCallback((path?: Array<ICategoryPath>) => {
		dispatch(resetMoBranchesTable());

		if (isDirtyMoTree) setIsDirtyMoTree(false);

		if (selectedPreset) setIsPresetTableView(true);

		setSubmittedPath(path);
	}, [dispatch, isDirtyMoTree, selectedPreset]);

	const vendor = watch(EParametersFormValues.vendor)?.value;
	const isMavenirVendor = useMemo(() => vendor === EVendorsValues.mavenir, [vendor]);
	const technology = watch(EParametersFormValues.technology)?.value;
	const nodeType = watch(EParametersFormValues.nodeType)?.value;
	const siteIds = (watch(EParametersFormValues.sites) as Array<IModalSelectOption>)
		.map(({ value }) => value);
	const nodes = watch(EParametersFormValues.nodes);
	const version = watch(EParametersFormValues.version)?.value;
	const type = watch(EParametersFormValues.type)?.value;
	const nodesValues = useMemo(() => nodes.map((node: TSelectOption<string>) => node?.value), [nodes]);
	const isDisabledSubmit = (isMavenirVendor
			? !nodes.length || !version || !type
			: !vendor || !technology || !nodeType || !siteIds.length)
		|| !!selectedPreset
		|| (watch(EParametersFormValues.isFirstCurrentDate) === false
			&& !watch(EParametersFormValues.dataSourceCreatedAtFirst))
		|| (watch(EParametersFormValues.isSecondCurrentDate) === false
			&& !watch(EParametersFormValues.dataSourceCreatedAtSecond));

	useEffect(() => {
		if (selectedVendor) {
			setValue(EParametersFormValues.vendor, selectedVendor);
		}
	}, [selectedVendor, setValue]);

	useEffect(() => {
		resetField(EParametersFormValues.technology);
		setValue(EParametersFormValues.nodes, []);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendor]);

	useEffect(() => {
		resetField(EParametersFormValues.version);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodes]);

	useEffect(() => {
		resetField(EParametersFormValues.type);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [version]);

	useEffect(() => {
		resetField(EParametersFormValues.nodeType);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [technology]);

	useEffect(() => {
		setValue(EParametersFormValues.sites, []);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodeType]);

	useEffect(() => {
		setValue(EParametersFormValues.dataSourceCreatedAtFirst, null);
		setValue(EParametersFormValues.dataSourceCreatedAtSecond, null);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type]);

	useEffect(() => {
		setValue(EParametersFormValues.dataSourceCreatedAtFirst, null);
		setValue(EParametersFormValues.dataSourceCreatedAtSecond, null);
		clearAllPreset();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const toggleComparisonOptions = () => {
		if (isOpenComparisonOptions) return setIsOpenComparisonOptions(false);

		setIsOpenComparisonOptions(true);
		setIsOpenMOTree(false);
	};

	const toggleMOTree = () => {
		if (isOpenMOTree) return setIsOpenMOTree(false);

		setIsOpenMOTree(true);
		setIsOpenComparisonOptions(false);
	};

	const openTableBranch = (data: IBranchTable) => setSelectedTableBranch(data);

	const closeDeleteConfirmPreset = () => setIsOpenDeleteConfirmPreset(false);

	const closeTableBranch = () => {
		setSelectedTableBranch(undefined);
		dispatch(resetMoBranchTable());
	};

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
	} = useAppSelector(moBranchesSelector);

	const {
		technologiesOptions,
		nodeTypesOptions,
		siteIdsOptions,
		datesOptions,
		isLoadingOptions,
		generateTree,
		moTree,
		runProgress,
		branchesList,
		countOfRecords,
		progress,
		isLoadingBranches,
		exportBranches,
		createTreePreset,
		treeId,
		presetsTreeOptions,
		getPresetTree,
		updateTreePreset,
		deleteTreePreset,
		nodesOptions,
		releaseVersionsOptions,
		configTypesOptions,
		datesMavenirOptions,
	} = useConfigurationComparison({
		vendor,
		technology,
		nodeType,
		siteIds: siteIds as Array<string>,
		toggleMOTree,
		submittedParams,
		submittedPath,
		isDifferenceOnly,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		setSavedPreset,
		savedPreset,
		selectedPreset,
		isPresetTableView,
		nodes: nodesValues,
		version,
		type,
	});

	const isShowPercentSpinner = !!progress?.percentsProgress && (progress?.percentsProgress < FULL_PERCENT);

	const columns = useMemo(() => getColumns({ openTableBranch }), []);

	const onSubmit = (values: FieldValues) => {
		generateTree(isMavenirVendor
			? {
				dataSourceCreatedAtFirstText: values.isFirstCurrentDate
					? null : formatDate(values.dataSourceCreatedAtFirst, 'dd/MM/yyyy'),
				dataSourceCreatedAtSecondText: values.isSecondCurrentDate
					? null : formatDate(values.dataSourceCreatedAtSecond, 'dd/MM/yyyy'),
				isFirstCurrentDate: !!values.isFirstCurrentDate,
				isSecondCurrentDate: !!values.isSecondCurrentDate,
				nodes: values.nodes.map(({ value }: { value: string }) => value),
				type: values.type.value,
				version: values.version.value,
				vendor: values.vendor.value,
			} : {
				dataSourceCreatedAtFirst: values.isFirstCurrentDate
					? null : startOfDayInUTC(new Date(values.dataSourceCreatedAtFirst)),
				dataSourceCreatedAtSecond: values.isSecondCurrentDate
					? null : startOfDayInUTC(new Date(values.dataSourceCreatedAtSecond)),
				isFirstCurrentDate: !!values.isFirstCurrentDate,
				isSecondCurrentDate: !!values.isSecondCurrentDate,
				nodeType: values.nodeType.value,
				siteIds: values.sites.map(({ value }: { value: string }) => value),
				technology: values.technology.value,
				vendor: values.vendor.value,
			});

		setSubmittedParams({ vendor: values.vendor.value as any } as any);

		if (!isMavenirVendor) {
			setSubmittedParams({
				nodeType: values.nodeType.value,
				technology: values.technology.value,
				vendor: values.vendor.value,
			});
		}

		setSubmittedPath([]);
		setIsDirtyMoTree(true);
		clearSelectPreset();
	};

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const onSearchHandler = (searchValue: string) => dispatch(setSearchValue({ searchValue }));

	const search: Array<ITableSearch> = [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
		},
	];

	const selectPreset = (preset: TSelectOptionType) => {
		setSelectedPreset(preset as TSelectOptionType);

		if (!preset) return;

		getPresetTree((preset as TSelectOptionType)?.value as string);

		if (!isOpenMOTree) toggleMOTree();

		clearResetPreset();

		if (preset?.siteIds?.length) {
			dispatch(setSnackbar({
				message: responseMessages.selectPresetCM(preset.siteIds),
				type: ESnackbarTypes.success,
			}));
		}
	};

	const handleSavePreset = ({ categoryPaths, name }: {
		categoryPaths: Array<ICategoryPath>;
		name: string;
	}) => {
		if (!submittedParams) return;

		createTreePreset({
			categoryPaths,
			name,
			nodeType: submittedParams.nodeType,
			technology: submittedParams.technology,
			vendor: submittedParams.vendor,
			onlyChanged: isDifferenceOnly,
		});
	};

	const openDeleteConfirmPreset = (option: TSelectOptionType) => {
		if (!option?.value) return;

		setIdForDeletePreset(option?.value as string);
		setIsOpenDeleteConfirmPreset(true);
	};

	const openPresetForm = (option: TSelectOptionType) => {
		if (!option?.value && !option?.label) return;

		setEditablePreset({
			id: option.value,
			name: option.label,
		} as TIdName);
	};

	const closePresetForm = () => setEditablePreset(undefined);

	const handleEditPreset = ({ name }: { name: string }) => {
		if (!editablePreset?.id) return;

		updateTreePreset({
			name,
			id: editablePreset.id,
		});

		closePresetForm();
		setSubmittedParams(undefined);
	};

	const handleDeletePreset = () => {
		deleteTreePreset({ id: idForDeletePreset });
		setIsOpenDeleteConfirmPreset(false);
	};

	useEffect(() => {
		if (!savedPreset) return;

		setValue(EParametersFormValues.dataSourceCreatedAtFirst, savedPreset.dataSourceCreatedAtFirst);
		setValue(EParametersFormValues.dataSourceCreatedAtSecond, savedPreset.dataSourceCreatedAtSecond);
	}, [savedPreset, setValue]);

	const handleExport = useCallback(() => {
		exportBranches({ branchIds: [], actionId: EMenuActionsTypes.all, presetDumpId: savedPreset?.dumpId });
	}, [exportBranches, savedPreset]);

	const isDisabledGenerateBranches = isShowPercentSpinner || !!progress?.isBranchDataLoadingInProgress;

	const isFeatureAvailable = useVendorDrivenFeatureAvailability();

	return isFeatureAvailable ? (
		<div className={styles.configurationComparisonContainer}>
			<Modal
				isOpen={isOpenDeleteConfirmPreset}
				onClose={closeDeleteConfirmPreset}
			>
				<Confirm
					onClose={closeDeleteConfirmPreset}
					onSubmit={handleDeletePreset}
					question='Are you sure you want to delete this preset?'
				/>
			</Modal>
			{selectedTableBranch && (
				<Modal
					isOpen={!isEmpty(selectedTableBranch)}
					onClose={closeTableBranch}
					title={selectedTableBranch?.distName}
					width='90%'
				>
					<BranchTable
						isMavenirVendor={isMavenirVendor}
						selectedTableBranch={selectedTableBranch}
					/>
				</Modal>
			)}
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={!!editablePreset}
				onClose={closePresetForm}
				title='Edit preset'
			>
				<PresetCcForm
					name={editablePreset?.name}
					onClose={closePresetForm}
					onSubmit={handleEditPreset as TOnSubmit}
					setIsDirtyFormModal={setIsDirtyFormModal}
				/>
			</Modal>
			<div className={styles.pageTitleWrapper}>
				<PageTitle
					icon={Icons.configurationIcon()}
					title={EAdminPanelPageTitles.ConfigurationComparison}
				/>
				{!isMavenirVendor
					&& <div className={styles.presetsSelect}>
						<Select
							control={control}
							disabled={!presetsTreeOptions.length}
							handleChange={selectPreset}
							isRenamable
							name='presets'
							onDeleteClick={openDeleteConfirmPreset}
							onRenameClick={openPresetForm}
							options={presetsTreeOptions}
							placeholder='Select preset'
							value={selectedPreset}
						/>
					</div>
				}
			</div>
			<div className={styles.configurationComparisonContent}>
				<ComparisonOptions
					configTypesOptions={configTypesOptions}
					control={control}
					datesMavenirOptions={datesMavenirOptions}
					datesOptions={datesOptions}
					errors={errors}
					handleSubmit={handleSubmit(onSubmit)}
					isDisabledSubmit={isDisabledSubmit || isDisabledGenerateBranches}
					isLoading={isLoadingOptions}
					isMavenirVendor={isMavenirVendor}
					isOpenComparisonOptions={isOpenComparisonOptions}
					nodeTypesOptions={nodeTypesOptions}
					nodesOptions={nodesOptions}
					register={register}
					releaseVersionsOptions={releaseVersionsOptions}
					savedPreset={savedPreset}
					selectedPreset={selectedPreset}
					setValue={setValue}
					siteIdsOptions={nodeType ? siteIdsOptions : []}
					technologiesOptions={technologiesOptions}
					toggleComparisonOptions={toggleComparisonOptions}
					watch={watch}
				/>
				<MOTree
					handleSavePreset={handleSavePreset}
					handleSubmitPath={handleSubmitPath}
					isDifferenceOnly={isDifferenceOnly}
					isDisabledSubmit={isDisabledGenerateBranches}
					isMavenir={isMavenirVendor}
					isOpenMOTree={isOpenMOTree}
					moTree={moTree}
					runProgress={runProgress}
					savedPreset={selectedPreset ? savedPreset : undefined}
					setIsDifferenceOnly={setIsDifferenceOnly}
					submittedParams={submittedParams}
					toggleMOTree={toggleMOTree}
					treeId={treeId}
				/>
				<div className={styles.configurationComparisonTableWrapper}>
					{(isDirtyMoTree ?? (!branchesList.length && !searchValue))
						? <div className={styles.emptyMessage}>
							Select options and apply MO Tree
						</div>
						: <Table
							columns={columns}
							data={branchesList}
							exportTooltipText='Export summary to Excel'
							handleCustomExport={handleExport}
							handleExport={handleExport}
							isExport
							isLoading={isLoadingBranches || progress?.percentsProgress !== FULL_PERCENT}
							isWithoutCheckbox
							isWithoutDeleteAction
							isWithoutFilter
							loadingComponent={isShowPercentSpinner
								? <PercentsSpinner
									percentsProgress={progress?.percentsProgress}
									savedBranches={progress?.savedBranches}
									totalBranchesToSave={progress?.totalBranchesToSave}
								/>
								: undefined}
							pagination={pagination}
							search={search}
							sort={sort}
						/>
					}
				</div>
			</div>
		</div>
	) : <UnavailableFeatureMessage vendorName={selectedVendor?.value} />;
};

export default memo(ConfigurationComparison);
